'use client';

import Pagination from '@/components/pagination';
import { UIPriority } from '@/enums/ui-priorities';
import Button from '@/ui/Button';
import Input from '@/ui/Input';
import Modal from '@/ui/Modal';
import Table, { TableCheckboxData, TableColumn } from '@/ui/Table';
import { FormEvent } from 'react';

type GroupEditorModalProps<TItem> = {
    isShown: boolean;
    title: string;
    onModalVisibilityChange: (isShown: boolean) => void;
    isLoading: boolean;
    loadingLabel: string;
    error: string | null;
    hasData: boolean;
    groupNameLabel: string;
    groupNameValue: string;
    onGroupNameChange: (value: string) => void;
    onGroupNameSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onGroupNameSave: () => void;
    isGroupNameSaving: boolean;
    canSaveGroupName: boolean;
    saveGroupNameLabel: string;
    saveGroupNamePendingLabel: string;
    totalLabel: string;
    totalValue: number;
    selectedLabel: string;
    selectedValue: number;
    tableColumns: TableColumn<TItem>[];
    tableData: TItem[];
    pickedData: unknown[];
    onHeaderCheckboxChange: (checked: boolean) => void;
    onCheckboxChange: (checked: boolean, item: TItem) => void;
    isHeaderChecked: boolean;
    checkboxData: TableCheckboxData[];
    emptyStateLabel: string;
    pageCount: number;
    page: number;
    setPage: (page: number) => void;
    rowsOnPage: number;
    onRowsOnPageUpdate: (count: number) => void;
    ungroupLabel: string;
    ungroupPendingLabel: string;
    isUngrouping: boolean;
    canUngroup: boolean;
    onUngroup: () => void;
    onClose: () => void;
    closeLabel: string;
};

export const GroupEditorModal = <TItem,>({
    isShown,
    title,
    onModalVisibilityChange,
    isLoading,
    loadingLabel,
    error,
    hasData,
    groupNameLabel,
    groupNameValue,
    onGroupNameChange,
    onGroupNameSubmit,
    onGroupNameSave,
    isGroupNameSaving,
    canSaveGroupName,
    saveGroupNameLabel,
    saveGroupNamePendingLabel,
    totalLabel,
    totalValue,
    selectedLabel,
    selectedValue,
    tableColumns,
    tableData,
    pickedData,
    onHeaderCheckboxChange,
    onCheckboxChange,
    isHeaderChecked,
    checkboxData,
    emptyStateLabel,
    pageCount,
    page,
    setPage,
    rowsOnPage,
    onRowsOnPageUpdate,
    ungroupLabel,
    ungroupPendingLabel,
    isUngrouping,
    canUngroup,
    onUngroup,
    onClose,
    closeLabel,
}: GroupEditorModalProps<TItem>) => {
    const resolveGroupEditorRowKey = (item: TItem, index: number): string | number => {
        if (item && typeof item === 'object' && 'id' in item) {
            const rawId = (item as { id?: unknown }).id;

            if (typeof rawId === 'string' || typeof rawId === 'number') {
                return rawId;
            }
        }

        return index;
    };

    return (
        <Modal
            isShown={isShown}
            setIsShown={onModalVisibilityChange}
            title={title}
            closable={true}
            className="h-[min(90vh,860px)] w-[min(1100px,96vw)]"
        >
            <div className="flex w-full min-h-0 flex-col gap-3 p-4">
                {isLoading && (
                    <div className="flex min-h-[120px] items-center justify-center text-sm text-slate-600">
                        {loadingLabel}
                    </div>
                )}
                {!isLoading && error && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {error}
                    </div>
                )}
                {!isLoading && !error && hasData && (
                    <>
                        <form className="grid grid-cols-5 items-end gap-2" onSubmit={onGroupNameSubmit}>
                            <div className="col-span-4 min-w-0">
                                <div className="mb-1 text-xs text-slate-500">{groupNameLabel}</div>
                                <Input
                                    id="group-editor-name-input"
                                    aria-label="Название группы"
                                    value={groupNameValue}
                                    onChange={(event) => onGroupNameChange(event.target.value)}
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) => event.stopPropagation()}
                                    className="w-full"
                                />
                            </div>
                            <Button
                                type="button"
                                className="col-span-1 w-full"
                                priorityType={UIPriority.SECONDARY}
                                onClick={onGroupNameSave}
                                disabled={isGroupNameSaving || !canSaveGroupName}
                            >
                                {isGroupNameSaving ? saveGroupNamePendingLabel : saveGroupNameLabel}
                            </Button>
                        </form>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>
                                {totalLabel}: {totalValue}
                            </span>
                            <span>
                                {selectedLabel}: {selectedValue}
                            </span>
                        </div>
                        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200 bg-white">
                            <Table
                                columns={tableColumns}
                                data={tableData}
                                rowKey={resolveGroupEditorRowKey}
                                tableId="group-editor-table"
                                tableScope="group-editor"
                                pickedData={pickedData}
                                onHeaderCheckboxChange={onHeaderCheckboxChange}
                                onCheckboxChange={onCheckboxChange}
                                isHeaderChecked={isHeaderChecked}
                                checkboxData={checkboxData}
                            />
                            {!totalValue && (
                                <div className="px-3 py-4 text-center text-sm text-slate-500">{emptyStateLabel}</div>
                            )}
                        </div>
                        <div className="flex justify-end">
                            <Pagination
                                numOfPages={pageCount}
                                currentPage={page}
                                setPage={setPage}
                                elementsOnPage={rowsOnPage}
                                onElementsOnPageUpdate={onRowsOnPageUpdate}
                                totalRows={totalValue}
                                presetValues={[rowsOnPage]}
                            />
                        </div>
                        <div className="mt-auto flex items-center justify-end gap-2 border-t border-slate-200 pt-3">
                            <Button
                                className="w-[170px]"
                                priorityType={canUngroup ? UIPriority.PRIMARY : UIPriority.SECONDARY}
                                onClick={onUngroup}
                                disabled={!canUngroup || isUngrouping}
                            >
                                {isUngrouping ? ungroupPendingLabel : ungroupLabel}
                            </Button>
                            <Button className="w-[120px]" priorityType={UIPriority.SECONDARY} onClick={onClose}>
                                {closeLabel}
                            </Button>
                        </div>
                    </>
                )}
                {(isLoading || error || !hasData) && (
                    <div className="mt-auto flex justify-end pt-2">
                        <Button className="w-[120px]" priorityType={UIPriority.SECONDARY} onClick={onClose}>
                            {closeLabel}
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};
